import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowLeft, Phone, Mail, ShieldCheck, AlertCircle, Pill, HeartPulse, FileText, Save, Lock, Sparkles,
  ClipboardList, Download, Plus, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const rand = (n) => "R" + Number(n || 0).toLocaleString("en-ZA");

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const clinicalAllowed = ["admin", "practitioner"].includes(user?.role);

  const { data: p, isLoading } = useQuery({
    queryKey: ["patient", id],
    queryFn: async () => (await api.get(`/patients/${id}`)).data,
  });

  if (isLoading || !p) return <div className="text-muted-foreground">Loading patient…</div>;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/app/patients")} data-testid="back-patients-btn" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> All patients
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-xl font-bold text-primary">
            {p.full_name?.charAt(0)}
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{p.full_name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <span>{p.file_number}</span>
              {p.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{p.phone}</span>}
              {p.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{p.email}</span>}
            </div>
          </div>
        </div>
        {p.medical_aid?.scheme && <Badge className="rounded-full">{p.medical_aid.scheme}</Badge>}
      </div>

      <Tabs defaultValue="overview">
        <TabsList data-testid="patient-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="clinical" data-testid="tab-clinical">Clinical</TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-plans">Treatment Plans</TabsTrigger>
          <TabsTrigger value="scripts" data-testid="tab-scripts">Scripts</TabsTrigger>
          <TabsTrigger value="forms" data-testid="tab-forms">Forms</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid gap-5 md:grid-cols-2">
            <Card className="rounded-2xl border-border/60 p-5">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-4 w-4 text-primary" /> Demographics</h3>
              <dl className="space-y-2 text-sm">
                <Row k="Date of birth" v={p.dob || "—"} />
                <Row k="SA ID number" v={p.id_number || "—"} />
                <Row k="Emergency contact" v={p.emergency_contact?.phone || "—"} />
              </dl>
            </Card>
            <Card className="rounded-2xl border-border/60 p-5">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><HeartPulse className="h-4 w-4 text-primary" /> Medical aid</h3>
              <dl className="space-y-2 text-sm">
                <Row k="Scheme" v={p.medical_aid?.scheme || "—"} />
                <Row k="Plan" v={p.medical_aid?.plan || "—"} />
                <Row k="Member no." v={p.medical_aid?.member_no || "—"} />
              </dl>
            </Card>
            {clinicalAllowed ? (
              <Card className="rounded-2xl border-border/60 p-5 md:col-span-2">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><AlertCircle className="h-4 w-4 text-primary" /> Clinical flags</h3>
                <div className="grid gap-4 sm:grid-cols-3 text-sm">
                  <Flags label="Allergies" items={p.allergies} icon={AlertCircle} />
                  <Flags label="Chronic conditions" items={p.chronic_conditions} icon={HeartPulse} />
                  <Flags label="Medications" items={p.current_medications} icon={Pill} />
                </div>
              </Card>
            ) : (
              <Card className="rounded-2xl border-border/60 bg-secondary/40 p-5 md:col-span-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Lock className="h-4 w-4 text-primary" /> Clinical details are masked for your role under POPIA data-minimisation.
                </div>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="clinical" className="mt-6">
          {clinicalAllowed ? <ClinicalTab patientId={id} patient={p} qc={qc} /> : (
            <Card className="rounded-2xl border-border/60 bg-secondary/40 p-8 text-center text-sm text-muted-foreground">
              <Lock className="mx-auto mb-2 h-6 w-6 text-primary" /> Clinical notes are restricted to practitioners.
            </Card>
          )}
        </TabsContent>

        <TabsContent value="plans" className="mt-6">
          {clinicalAllowed ? <PlansTab patientId={id} qc={qc} /> : <Restricted />}
        </TabsContent>

        <TabsContent value="scripts" className="mt-6">
          {clinicalAllowed ? <ScriptsTab patientId={id} qc={qc} /> : <Restricted />}
        </TabsContent>

        <TabsContent value="forms" className="mt-6">
          {clinicalAllowed ? <FormsRespTab patientId={id} /> : <Restricted />}
        </TabsContent>

        <TabsContent value="invoices" className="mt-6">
          <InvoicesTab patientId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="font-medium text-right">{v}</dd>
    </div>
  );
}

function Flags({ label, items, icon: Icon }) {
  return (
    <div>
      <div className="label-eyebrow mb-2 flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</div>
      {items?.length ? (
        <div className="flex flex-wrap gap-1.5">
          {items.map((i) => <Badge key={i} variant="outline" className="rounded-full">{i}</Badge>)}
        </div>
      ) : <span className="text-muted-foreground">None recorded</span>}
    </div>
  );
}

function ClinicalTab({ patientId, patient, qc }) {
  const [draft, setDraft] = useState({ subjective: "", objective: "", assessment: "", plan: "" });
  const [transcript, setTranscript] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const { data: notes = [] } = useQuery({
    queryKey: ["clinical", patientId],
    queryFn: async () => (await api.get(`/patients/${patientId}/clinical`)).data,
  });

  const createMut = useMutation({
    mutationFn: async (body) => (await api.post("/clinical", { patient_id: patientId, ...body })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["clinical", patientId] }); setDraft({ subjective: "", objective: "", assessment: "", plan: "" }); setTranscript(""); toast.success("SOAP note saved"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const signMut = useMutation({
    mutationFn: async (nid) => (await api.post(`/clinical/${nid}/sign`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["clinical", patientId] }); toast.success("Note signed & locked"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const aiDraft = async () => {
    if (!transcript.trim()) return toast.error("Add some dictation first");
    setAiLoading(true);
    try {
      const ctx = `Patient ${patient.full_name}. Allergies: ${(patient.allergies || []).join(", ") || "none"}. Conditions: ${(patient.chronic_conditions || []).join(", ") || "none"}.`;
      const { data } = await api.post("/ai/soap-draft", { transcript, patient_context: ctx });
      setDraft({ subjective: data.subjective, objective: data.objective, assessment: data.assessment, plan: data.plan });
      toast.success("AI drafted your SOAP note — review & edit");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="rounded-2xl border-border/60 p-5">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Sparkles className="h-4 w-4 text-primary" /> New SOAP note</h3>
        <Textarea data-testid="soap-transcript" placeholder="Dictate freely — e.g. 'Patient here with cold sensitivity upper right for two weeks, tooth 16 deep caries, plan composite restoration…'" value={transcript} onChange={(e) => setTranscript(e.target.value)} className="min-h-[80px]" />
        <Button data-testid="ai-draft-btn" onClick={aiDraft} disabled={aiLoading} variant="outline" className="mt-3 gap-2 rounded-full">
          <Sparkles className="h-4 w-4" /> {aiLoading ? "Drafting…" : "Draft SOAP with AI"}
        </Button>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {["subjective", "objective", "assessment", "plan"].map((k) => (
            <div key={k}>
              <label className="label-eyebrow mb-1 block capitalize">{k}</label>
              <Textarea data-testid={`soap-${k}`} value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} className="min-h-[70px]" />
            </div>
          ))}
        </div>
        <Button data-testid="soap-save-btn" onClick={() => createMut.mutate({ ...draft, ai_draft: !!transcript })} disabled={createMut.isPending} className="mt-4 gap-2 rounded-full">
          <Save className="h-4 w-4" /> Save note
        </Button>
      </Card>

      <div className="space-y-3">
        {notes.map((n) => (
          <Card key={n.id} data-testid={`note-${n.id}`} className="rounded-2xl border-border/60 p-5">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold"><FileText className="h-4 w-4 text-primary" /> SOAP note</div>
              {n.signed_at ? (
                <Badge variant="secondary" className="gap-1 rounded-full"><Lock className="h-3 w-3" /> Signed · {n.signed_by}</Badge>
              ) : (
                <Button size="sm" data-testid={`sign-${n.id}`} onClick={() => signMut.mutate(n.id)} className="rounded-full">Sign & lock</Button>
              )}
            </div>
            <dl className="space-y-2 text-sm">
              {["subjective", "objective", "assessment", "plan"].map((k) => n[k] && (
                <div key={k}><span className="label-eyebrow capitalize">{k}</span><p className="mt-0.5">{n[k]}</p></div>
              ))}
            </dl>
          </Card>
        ))}
      </div>
    </div>
  );
}

function FormsRespTab({ patientId }) {
  const { data: responses = [] } = useQuery({
    queryKey: ["patient-forms", patientId],
    queryFn: async () => (await api.get(`/patients/${patientId}/forms`)).data,
  });
  const downloadFormPdf = async (rid) => {
    try {
      const res = await api.get(`/form-responses/${rid}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(apiErr(e)); }
  };
  if (!responses.length) return <Card className="rounded-2xl border-border/60 p-8 text-center text-sm text-muted-foreground">No forms submitted yet.</Card>;
  return (
    <div className="space-y-3">
      {responses.map((r) => (
        <Card key={r.id} data-testid={`patient-form-${r.id}`} className="rounded-2xl border-border/60 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-semibold"><ClipboardList className="h-4 w-4 text-primary" /> {r.form_title}</div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="rounded-full">{new Date(r.submitted_at).toLocaleDateString("en-ZA")}</Badge>
              <Button size="sm" variant="ghost" data-testid={`form-pdf-${r.id}`} onClick={() => downloadFormPdf(r.id)} className="gap-1 rounded-full"><Download className="h-3.5 w-3.5" /> PDF</Button>
            </div>
          </div>
          <dl className="mt-3 space-y-1.5 text-sm">
            {Object.entries(r.responses || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 border-b border-border/40 py-1.5 last:border-0">
                <dt className="capitalize text-muted-foreground">{k.replace(/_/g, " ")}</dt>
                <dd className="text-right font-medium">{Array.isArray(v) ? v.join(", ") : String(v)}</dd>
              </div>
            ))}
          </dl>
        </Card>
      ))}
    </div>
  );
}

function InvoicesTab({ patientId }) {
  const { data: invoices = [] } = useQuery({
    queryKey: ["invoices"],
    queryFn: async () => (await api.get("/invoices")).data,
  });
  const mine = invoices.filter((i) => i.patient_id === patientId);
  if (!mine.length) return <Card className="rounded-2xl border-border/60 p-8 text-center text-sm text-muted-foreground">No invoices yet.</Card>;
  return (
    <div className="space-y-3">
      {mine.map((i) => (
        <Card key={i.id} className="flex items-center justify-between rounded-2xl border-border/60 p-4">
          <div>
            <div className="font-medium">{i.receipt_no}</div>
            <div className="text-xs text-muted-foreground">{i.line_items?.map((l) => l.desc).join(", ")}</div>
          </div>
          <div className="text-right">
            <div className="font-semibold">{rand(i.total)}</div>
            <Badge variant={i.status === "paid" ? "secondary" : "outline"} className="rounded-full capitalize">{i.status}</Badge>
          </div>
        </Card>
      ))}
    </div>
  );
}

function Restricted() {
  return (
    <Card className="rounded-2xl border-border/60 bg-secondary/40 p-8 text-center text-sm text-muted-foreground">
      <Lock className="mx-auto mb-2 h-6 w-6 text-primary" /> Restricted to practitioners under POPIA.
    </Card>
  );
}

function PlansTab({ patientId, qc }) {
  const [form, setForm] = useState({ title: "", phases: "", medical_aid_covered_zar: "" });
  const { data: plans = [] } = useQuery({
    queryKey: ["plans", patientId],
    queryFn: async () => (await api.get(`/patients/${patientId}/treatment-plans`)).data,
  });
  const createMut = useMutation({
    mutationFn: async (body) => (await api.post("/treatment-plans", body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["plans", patientId] }); setForm({ title: "", phases: "", medical_aid_covered_zar: "" }); toast.success("Treatment plan created"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const consentMut = useMutation({
    mutationFn: async (id) => (await api.post(`/treatment-plans/${id}/consent`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["plans", patientId] }); toast.success("Consent signed & timestamped"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const submit = () => {
    const phases = form.phases.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const [name, cost] = l.split("|");
      return { name: (name || "").trim(), cost_zar: Number((cost || "0").trim()) || 0 };
    });
    createMut.mutate({ patient_id: patientId, title: form.title, phases, medical_aid_covered_zar: Number(form.medical_aid_covered_zar || 0) });
  };

  return (
    <div className="space-y-6">
      <Card className="rounded-2xl border-border/60 p-5">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><ClipboardList className="h-4 w-4 text-primary" /> New treatment plan</h3>
        <div className="space-y-3">
          <div><Label>Title</Label><Input data-testid="plan-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Full-mouth rehabilitation" /></div>
          <div><Label>Phases — one per line as <code>Name | cost</code></Label>
            <Textarea data-testid="plan-phases" value={form.phases} onChange={(e) => setForm({ ...form, phases: e.target.value })} className="min-h-[90px]" placeholder={"Deep clean | 950\nTwo fillings | 1900\nCrown on 26 | 6500"} />
          </div>
          <div className="max-w-xs"><Label>Medical aid covered (R)</Label><Input type="number" data-testid="plan-covered" value={form.medical_aid_covered_zar} onChange={(e) => setForm({ ...form, medical_aid_covered_zar: e.target.value })} /></div>
        </div>
        <Button data-testid="plan-save-btn" onClick={submit} disabled={!form.title || createMut.isPending} className="mt-4 gap-2 rounded-full"><Plus className="h-4 w-4" /> Create plan</Button>
      </Card>

      {plans.map((pl) => (
        <Card key={pl.id} data-testid={`plan-${pl.id}`} className="rounded-2xl border-border/60 p-5">
          <div className="flex items-start justify-between">
            <div className="font-semibold">{pl.title}</div>
            {pl.consent_signed_at ? (
              <Badge variant="secondary" className="gap-1 rounded-full"><CheckCircle2 className="h-3 w-3" /> Consented</Badge>
            ) : (
              <Button size="sm" data-testid={`consent-${pl.id}`} onClick={() => consentMut.mutate(pl.id)} className="rounded-full">Sign consent</Button>
            )}
          </div>
          <div className="mt-3 space-y-1.5 text-sm">
            {pl.phases?.map((ph, i) => (
              <div key={i} className="flex justify-between border-b border-border/40 py-1.5 last:border-0">
                <span>{ph.name}</span><span className="font-medium">{rand(ph.cost_zar)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center text-sm">
            <div className="rounded-lg bg-secondary/50 p-2"><div className="font-bold">{rand(pl.total_estimate_zar)}</div><div className="text-[10px] uppercase text-muted-foreground">Estimate</div></div>
            <div className="rounded-lg bg-secondary/50 p-2"><div className="font-bold">{rand(pl.medical_aid_covered_zar)}</div><div className="text-[10px] uppercase text-muted-foreground">Med-aid</div></div>
            <div className="rounded-lg bg-secondary/50 p-2"><div className="font-bold">{rand(pl.patient_liable_zar)}</div><div className="text-[10px] uppercase text-muted-foreground">Patient</div></div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function ScriptsTab({ patientId, qc }) {
  const [pharmacy, setPharmacy] = useState("");
  const [itemsText, setItemsText] = useState("");
  const { data: scripts = [] } = useQuery({
    queryKey: ["scripts", patientId],
    queryFn: async () => (await api.get(`/patients/${patientId}/prescriptions`)).data,
  });
  const createMut = useMutation({
    mutationFn: async (body) => (await api.post("/prescriptions", body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["scripts", patientId] }); setItemsText(""); setPharmacy(""); toast.success("Script issued — sent to pharmacy via n8n"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const submit = () => {
    const items = itemsText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const [drug, dose, freq, days] = l.split("|").map((x) => (x || "").trim());
      return { drug, dose, freq, days: Number(days) || 0 };
    });
    if (!items.length) return toast.error("Add at least one medication");
    createMut.mutate({ patient_id: patientId, items, pharmacy });
  };

  const download = async (id) => {
    try {
      const res = await api.get(`/prescriptions/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6">
      <Card className="rounded-2xl border-border/60 p-5">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Pill className="h-4 w-4 text-primary" /> New prescription</h3>
        <div className="space-y-3">
          <div><Label>Medications — one per line as <code>Drug | dose | freq | days</code></Label>
            <Textarea data-testid="script-items" value={itemsText} onChange={(e) => setItemsText(e.target.value)} className="min-h-[90px]" placeholder={"Amoxicillin | 500mg | 3x daily | 5\nIbuprofen | 400mg | as needed | 3"} />
          </div>
          <div className="max-w-sm"><Label>Pharmacy (optional)</Label><Input data-testid="script-pharmacy" value={pharmacy} onChange={(e) => setPharmacy(e.target.value)} placeholder="Clicks Sandton City" /></div>
        </div>
        <Button data-testid="script-save-btn" onClick={submit} disabled={createMut.isPending} className="mt-4 gap-2 rounded-full"><Plus className="h-4 w-4" /> Issue script</Button>
      </Card>

      {scripts.map((rx) => (
        <Card key={rx.id} data-testid={`script-${rx.id}`} className="rounded-2xl border-border/60 p-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="font-semibold">Prescription · {new Date(rx.created_at).toLocaleDateString("en-ZA")}</div>
              <div className="text-xs text-muted-foreground">{rx.practitioner_name}{rx.pharmacy ? ` · ${rx.pharmacy}` : ""}</div>
            </div>
            <Button size="sm" variant="outline" data-testid={`script-pdf-${rx.id}`} onClick={() => download(rx.id)} className="gap-1 rounded-full">
              <Download className="h-3.5 w-3.5" /> PDF
            </Button>
          </div>
          <ul className="mt-3 space-y-1 text-sm">
            {rx.items?.map((it, i) => (
              <li key={i} className="flex items-center gap-2"><Pill className="h-3.5 w-3.5 text-primary" /> {it.drug} {it.dose} — {it.freq} for {it.days} days</li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
}
